import { RotateCcw, Search, SlidersHorizontal, X } from "lucide-react";
import { useState } from "react";
import Button from "@/components/ui/Button";
import Input from "@/components/ui/Input";
import Modal from "@/components/ui/Modal";
import Select from "@/components/ui/Select";
import { SORT_OPTIONS } from "@/pages/admin/retailers/data";

interface Chip {
  label: string;
  onRemove: () => void;
}

interface RetailerFilterBarProps {
  search: string;
  onSearch: (v: string) => void;
  status: string;
  onStatus: (v: string) => void;
  city: string;
  onCity: (v: string) => void;
  cities: string[];
  sort: string;
  onSort: (v: string) => void;
  onReset: () => void;
  chips: Chip[];
}

export default function RetailerFilterBar({
  search,
  onSearch,
  status,
  onStatus,
  city,
  onCity,
  cities,
  sort,
  onSort,
  onReset,
  chips,
}: RetailerFilterBarProps) {
  const [filtersOpen, setFiltersOpen] = useState(false);

  const statusSelect = (
    <div className="lg:w-40">
      <Select
        value={status}
        onChange={(e) => onStatus(e.target.value)}
        options={[
          { value: "Active", label: "Active" },
          { value: "Inactive", label: "Inactive" },
        ]}
        placeholder="All Status"
      />
    </div>
  );

  const citySelect = (
    <div className="lg:w-40">
      <Select
        value={city}
        onChange={(e) => onCity(e.target.value)}
        options={cities.map((c) => ({ value: c, label: c }))}
        placeholder="All Cities"
      />
    </div>
  );

  const sortSelect = (
    <div className="lg:w-48">
      <Select value={sort} onChange={(e) => onSort(e.target.value)} options={SORT_OPTIONS} />
    </div>
  );

  return (
    <div className="rounded-[10px] border border-line bg-white p-4 shadow-card">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
        <div className="flex flex-1 items-center gap-3">
          <Input
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            prefixIcon={<Search className="h-4 w-4" />}
            placeholder="Search by shop name, owner, mobile or retailer ID"
            className="flex-1"
          />
          <Button
            variant="secondary"
            onClick={() => setFiltersOpen(true)}
            leftIcon={<SlidersHorizontal className="h-4 w-4" />}
            className="lg:hidden"
          >
            Filters
          </Button>
        </div>

        <div className="hidden items-center gap-3 lg:flex">
          {statusSelect}
          {citySelect}
          {sortSelect}
          <Button variant="ghost" onClick={onReset} leftIcon={<RotateCcw className="h-4 w-4" />}>
            Reset
          </Button>
        </div>
      </div>

      {chips.length > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-line pt-3">
          {chips.map((c, i) => (
            <span
              key={i}
              className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-primary-50 px-2.5 py-1 text-xs font-medium text-primary-600"
            >
              {c.label}
              <button
                type="button"
                onClick={c.onRemove}
                className="flex h-4 w-4 items-center justify-center rounded-full hover:bg-primary-100 cursor-pointer"
                aria-label="Remove filter"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      <Modal open={filtersOpen} onClose={() => setFiltersOpen(false)} title="Filters">
        <div className="space-y-4">
          <div>{statusSelect}</div>
          <div>{citySelect}</div>
          <div>{sortSelect}</div>
          <Button
            variant="secondary"
            fullWidth
            onClick={() => {
              onReset();
              setFiltersOpen(false);
            }}
            leftIcon={<RotateCcw className="h-4 w-4" />}
          >
            Reset Filters
          </Button>
        </div>
      </Modal>
    </div>
  );
}